import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

import { type ChartDataPoint, fetchSingleChart } from '@/shared/api/openmetadata';
import { convertPercentageToFixed } from '@/shared/lib/utils/number';

import type { DashboardEntityCountsResponse } from './dashboardEntityCountsApi';
import type { AssetStats, DailyAssetData, DataAssetsOverview, DataInsightsResponse } from './types';

const TOTAL_DATA_ASSETS_CHART_NAME = 'total_data_assets';

/** 홈 총 데이터 자산 위젯에서 집계·도넛에서 제외 (OM 차트에 포함될 수 있음) */
const EXCLUDED_DASHBOARD_ASSET_GROUPS = new Set(['mlmodel']);

/**
 * Cyclone `entity-counts` 만: 런에 종속된 조인 엔티티는 도넛에 합산하지 않음 (OM `total_data_assets` 와 중복·노이즈 방지).
 */
const EXCLUDED_CYCLONE_RUN_DEPENDENT_GROUPS = new Set([
  'run_dataset',
  'run_feature_set',
  'run_parameter_set',
  'run_artifact',
]);

/**
 * Cyclone 수치를 OM 일별 스냅샷에 합칠 때만 덮어쓸 그룹 (카탈로그 OM vs 플랫폼 Cyclone max).
 */
export const DASHBOARD_ALLOWED_ENTITY_GROUPS = ['experiment', 'run', 'feature_set', 'dataset'] as const;

type DailyGroupData = {
  groups: Record<string, number>;
  total: number;
};

const DEFAULT_DAILY_DATA: DailyGroupData = {
  groups: {},
  total: 0,
};

function dayHasAggregatedCounts(d: DailyGroupData): boolean {
  return d.total > 0 || Object.values(d.groups).some((c) => c > 0);
}

/**
 * API에 집계가 있는 가장 늦은 날짜(오늘 이하).
 * 그 날짜보다 뒤의 기간 슬롯(최신 집계일~오늘 사이의 빈 날 전부)은 이 스냅샷으로 맞춘다.
 */
function findLatestSnapshotDateKey(dailyDataMap: Record<string, DailyGroupData>, todayStr: string): string | undefined {
  const keys = Object.keys(dailyDataMap).filter((d) => d <= todayStr);
  keys.sort();
  for (let i = keys.length - 1; i >= 0; i--) {
    const dd = dailyDataMap[keys[i]];
    if (dayHasAggregatedCounts(dd)) {
      return keys[i];
    }
  }
  return undefined;
}

/**
 * 빈 날짜 슬롯을 가장 최근 스냅샷으로 채운다 (API가 오늘 집계를 아직 안 줬을 때 대응).
 */
function fillEmptyDatesWithSnapshot(
  allDates: DailyAssetData[],
  dailyDataMap: Record<string, DailyGroupData>,
  todayStr: string
): void {
  const latestKey = findLatestSnapshotDateKey(dailyDataMap, todayStr);
  if (!latestKey) return;

  const snap = dailyDataMap[latestKey] ?? DEFAULT_DAILY_DATA;
  for (let i = 0; i < allDates.length; i++) {
    if (allDates[i].date > latestKey) {
      allDates[i] = {
        ...allDates[i],
        groups: { ...snap.groups },
        total: snap.total,
      };
    }
  }
}

const EMPTY_ASSET_STATS: AssetStats = {
  totalAssets: 0,
  yesterdayAssets: 0,
  changeRate: '0%',
  dailyCollection: 0,
  dailyChangeRate: '0%',
};

export const fetchTotalDataAssets = async (period: 7 | 14 | 30 | 90 = 7): Promise<DataInsightsResponse> => {
  const end = dayjs().valueOf();
  const start = dayjs().subtract(period, 'day').valueOf();
  return fetchSingleChart({
    chartName: TOTAL_DATA_ASSETS_CHART_NAME,
    startDate: start,
    endDate: end,
  });
};

function resolveCycloneGroup(r: DashboardEntityCountsResponse['results'][number]): string | undefined {
  const g = r.group ?? r.entityType ?? r.entity_type;
  return typeof g === 'string' && g.length > 0 ? g : undefined;
}

function resolveCycloneDay(r: DashboardEntityCountsResponse['results'][number]): number | undefined {
  const d = r.day ?? r.timestamp;
  if (d === undefined) return undefined;
  if (typeof d === 'number' && !Number.isNaN(d)) return d;
  if (typeof d === 'string') {
    const n = Number(d);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
}

function resolveCycloneCount(r: DashboardEntityCountsResponse['results'][number]): number {
  if (typeof r.count === 'number' && !Number.isNaN(r.count)) return r.count;
  if (typeof r.count === 'string') {
    const n = Number(r.count);
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
}

/** 도넛/카드에 쓸 수 있는 일별 그룹 합계가 있는지 */
export const isDataAssetsOverviewEmpty = (overview: DataAssetsOverview): boolean => {
  if (overview.dailyData.length === 0) return true;
  return !overview.dailyData.some((day) => Object.values(day.groups).some((c) => c > 0));
};

/**
 * OpenMetadata 전체 그룹(테이블 등)은 유지하고, Cyclone 과 겹치는 네 그룹만 일별 max 로 보정한다.
 * `total` 은 병합 후 그룹 합으로 다시 계산한다.
 */
export const mergeCycloneIntoOpenMetadataOverview = (
  openMetadataOverview: DataAssetsOverview,
  cycloneOverview: DataAssetsOverview
): DataAssetsOverview => {
  const dailyData = openMetadataOverview.dailyData.map((omDay, i) => {
    const cyDay = cycloneOverview.dailyData[i];
    const groups: Record<string, number> = { ...omDay.groups };
    for (const g of DASHBOARD_ALLOWED_ENTITY_GROUPS) {
      const o = omDay.groups[g] ?? 0;
      const c = cyDay?.groups[g] ?? 0;
      groups[g] = Math.max(o, c);
    }
    const total = Object.values(groups).reduce((sum, n) => sum + n, 0);
    return {
      ...omDay,
      groups,
      total,
    };
  });

  return {
    totalAssets: dailyData[dailyData.length - 1]?.total ?? 0,
    dailyData,
    period: openMetadataOverview.period,
  };
};

/** Cyclone entity-counts → OpenMetadata `total_data_assets` 차트와 동일한 `ChartData` 형태 */
export const cycloneEntityCountsToChartData = (res: DashboardEntityCountsResponse): DataInsightsResponse => {
  const results: ChartDataPoint[] = [];
  for (const r of res.results ?? []) {
    const group = resolveCycloneGroup(r);
    const day = resolveCycloneDay(r);
    if (group === undefined || day === undefined) continue;
    if (EXCLUDED_DASHBOARD_ASSET_GROUPS.has(group)) continue;
    if (EXCLUDED_CYCLONE_RUN_DEPENDENT_GROUPS.has(group)) continue;
    results.push({ day, group, count: resolveCycloneCount(r) });
  }
  return { results };
};

export const calculateAssetStats = (data: DataInsightsResponse): AssetStats => {
  const { results } = data;

  if (!results || results.length === 0) {
    return EMPTY_ASSET_STATS;
  }

  const filteredResults = results.filter(
    (item) => item.group === undefined || !EXCLUDED_DASHBOARD_ASSET_GROUPS.has(item.group)
  );

  if (filteredResults.length === 0) {
    return EMPTY_ASSET_STATS;
  }

  const groupedByDay = filteredResults.reduce(
    (acc, item) => {
      const day = item.day;
      if (day !== undefined) {
        if (!acc[day]) {
          acc[day] = [];
        }
        acc[day].push(item);
      }
      return acc;
    },
    {} as Record<number, typeof filteredResults>
  );

  const sortedDays = Object.keys(groupedByDay)
    .map(Number)
    .sort((a, b) => b - a);

  if (sortedDays.length === 0) {
    return EMPTY_ASSET_STATS;
  }

  const latestDay = sortedDays[0];
  const previousDay = sortedDays[1];
  const twoDaysAgo = sortedDays[2];

  const getCountForDay = (dayKey: number | undefined): number => {
    if (!dayKey || !groupedByDay[dayKey]) return 0;
    return groupedByDay[dayKey].reduce((sum, item) => sum + item.count, 0);
  };

  const totalAssets = getCountForDay(latestDay);
  const yesterdayAssets = previousDay ? getCountForDay(previousDay) : 0;
  const changeRateValue = yesterdayAssets > 0 ? ((totalAssets - yesterdayAssets) / yesterdayAssets) * 100 : 0;
  const dailyCollection = totalAssets - yesterdayAssets;
  const previousDailyCollection =
    previousDay && twoDaysAgo ? getCountForDay(previousDay) - getCountForDay(twoDaysAgo) : 0;
  const dailyChangeRateValue =
    previousDailyCollection > 0 ? ((dailyCollection - previousDailyCollection) / previousDailyCollection) * 100 : 0;

  const changeRate = convertPercentageToFixed(changeRateValue.toString());
  const dailyChangeRate = convertPercentageToFixed(dailyChangeRateValue.toString());

  return {
    totalAssets,
    yesterdayAssets,
    changeRate,
    dailyCollection,
    dailyChangeRate,
  };
};

export const processDataAssetsOverview = (
  data: DataInsightsResponse,
  period: 7 | 14 | 30 | 90 = 7,
  referenceDay?: Dayjs
): DataAssetsOverview => {
  const results = data.results ?? [];

  const dailyDataMap = results.reduce(
    (acc, item) => {
      const day = item.day;
      const group = item.group;
      if (day !== undefined && group !== undefined) {
        if (EXCLUDED_DASHBOARD_ASSET_GROUPS.has(group)) {
          return acc;
        }
        const dateString = dayjs(day).format('YYYY-MM-DD');
        if (!acc[dateString]) {
          acc[dateString] = { groups: {}, total: 0 };
        }
        const prevGroup = acc[dateString].groups[group] ?? 0;
        acc[dateString].groups[group] = prevGroup + item.count;
        acc[dateString].total += item.count;
      }
      return acc;
    },
    {} as Record<string, DailyGroupData>
  );

  const today = (referenceDay ?? dayjs()).startOf('day');
  const todayStr = today.format('YYYY-MM-DD');
  const allDates: DailyAssetData[] = [];

  for (let i = period - 1; i >= 0; i--) {
    const currentDate = today.subtract(i, 'day');
    const dateString = currentDate.format('YYYY-MM-DD');
    const dayTimestamp = currentDate.valueOf();

    const dayData = dailyDataMap[dateString] ?? DEFAULT_DAILY_DATA;

    allDates.push({
      date: dateString,
      day: dayTimestamp,
      groups: { ...dayData.groups },
      total: dayData.total,
      formattedDate: currentDate.format('DD MMM'),
    });
  }

  fillEmptyDatesWithSnapshot(allDates, dailyDataMap, todayStr);

  const totalAssets = allDates[allDates.length - 1]?.total || 0;

  return {
    totalAssets,
    dailyData: allDates,
    period,
  };
};
